import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useJobFilterStore } from "../../store/useJobFilterStore";
import { exportCV } from "../../utils/exportCV";
import { findLabel } from "../../utils/mapLabel";
import { useCandidateProfileStore } from "../../store/useCandidateProfileStore";

import MainLayoutUser from "../../components/layout/MainLayoutUser";

import SectionBox from "../../components/layout/SectionBox";
import UserWorkExperience from "../../components/molecules/UserWorkExperience";
import UserBasicInformation from "../../components/molecules/UserBasicInformation";
import UserEducation from "../../components/molecules/UserEducation";
import UserSalary from "../../components/molecules/UserSalary";
import UserSkill from "../../components/molecules/UserSkill";
import UserResume from "../../components/molecules/UserResume";
import UserCertification from "../../components/molecules/UserCertification";
import UserProject from "../../components/molecules/UserProject";
import UserLanguage from "../../components/molecules/UserLanguage";



import UserProfileHeader from "../../components/molecules/UserProfileHeader";
import EditProfileModal from "../../components/modals/EditProfileModal";
import SidebarRight from "../../components/molecules/SidebarRight";

import PageLoader from "../../components/atoms/PageLoader";

export default function UserProfile() {
    const location = useLocation();
    const lang = location.pathname.startsWith("/id") ? "id" : "en";

    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState("");

    // ================== PROFILE ==================
    const { profile, fetchCandidateProfile } = useCandidateProfileStore();

    useEffect(() => {
        fetchCandidateProfile();
    }, [fetchCandidateProfile]);

    // ================== FILTER STORE ==================
    const {
        industries,
        jobTypes,
        degrees,
        skillSet,
        genders,
        languageSet,
        proficiencies,
        experiences,
        experienceLevels,
        countries,
        states,
        cities,
        martialStatus,
        fetchStates,
        fetchCities,
        fetchFilters,
    } = useJobFilterStore();

    // ================== LOCATION STATE ==================
    const [countryId, setCountryId] = useState<string>("");
    const [stateId, setStateId] = useState<string>("");
    const [cityId, setCityId] = useState<string>("");
    const [project, setProject] = useState([
        {
            projectTitle: "Project 1",
            startDate: "2024-01",
            endDate: "2025-01",
            current: false,
            projectDescription: "Desc 1",
        },
        {
            projectTitle: "Project 2",
            startDate: "2025-01",
            endDate: "",
            current: true,
            projectDescription: "Desc 2",
        },
    ]);

    // ================== FETCH FILTER MASTER ==================
    useEffect(() => {
        if (!lang) return;

        setLoading(true);
        fetchFilters(lang).finally(() => setLoading(false));
    }, [lang, fetchFilters]);

    // ================== COUNTRY → STATE ==================
    useEffect(() => {
        if (!countryId) {
            setStateId("");
            setCityId("");
            return;
        }

        fetchStates(Number(countryId), lang);
        setStateId("");
        setCityId("");
    }, [countryId, lang, fetchStates]);

    // ================== STATE → CITY ==================
    useEffect(() => {
        if (!stateId) {
            setCityId("");
            return;
        }

        fetchCities(Number(stateId), lang);
        setCityId("");
    }, [stateId, lang, fetchCities]);

    // ================== BASIC INFORMATION ==================
    const [basicInformation, setBasicInformation] = useState<any[]>([]);
    const [basicInformationCv, setBasicInformationCv] = useState<any[]>([]);
    const [salary, setSalary] = useState<any[]>([]);
    const [resume, setResume] = useState<any[]>([]);
    const [skill, setSkill] = useState<any[]>([]);
    const [language, setLanguage] = useState<any[]>([]);
    const [workExperience, setWorkExperience] = useState<any[]>([]);
    const [education, setEducation] = useState<any[]>([]);
    const [certificate, setCertificate] = useState<any[]>([]);

    const toMonth = (date?: string) => {
        if (!date) return "";
        return date.slice(0, 10); // "2024-12"
    };

    useEffect(() => {
        if (!profile) return;

        setBasicInformation([
            {
                imgProfile: profile.photo || "https://placehold.co/200",
                name: profile.name,
                phone: profile.phone,
                country: profile.country_id,
                state: profile.state_id,
                city: profile.city_id,
                gender: profile.gender_id,
                experience: profile.experience_year_id,
                experienceLevel: profile.experience_level_id,
                martialStatus: profile.martial_status_id,
                degree: profile.degree_id,
                birth: profile.birth_date,
                about: profile.about.en,
            },
        ]);

        setBasicInformationCv([
            {
                imgProfile: profile.photo || "https://placehold.co/200",
                name: profile.name,
                phone: profile.phone,
                country: profile.country_name,
                state: profile.state_name,
                city: profile.city_name,
                gender: profile.gender_name?.en,
                experience: profile.experience_year_name?.en,
                experienceLevel: profile.experience_level_name?.en,
                martialStatus: profile.martial_status_name?.en,
                degree: profile.degree_name?.en,
                birth: profile.birth_date,
                about: profile.about?.en,
            },
        ]);

        setSalary([
            {
                currentSalary: profile.current_salary,
                currentSalaryMethod: profile.current_salary_method,
                expectedSalary: profile.expected_salary,
                expectedSalaryMethod: profile.expected_salary_method,
                negotiable: profile.is_negotiable,
            },
        ]);

        setResume([
            {
                resumeUrl: profile.resume,
                resumeFile: null,
                portfolio: profile.website,
            },
        ]);

        setSkill(
            profile.skills?.map((s: any) => ({
                skillName: s.id,
            })) || []
        );

        setLanguage(
            profile.languages?.map((l: any) => ({
                languageId: l.id,
                profiencyId: l.proficiency?.id,
            })) || []
        );

        setWorkExperience(
            profile.work_experiences?.map((w: any) => ({
                jobTitle: w.job_title,
                companyName: w.name,
                industry: w.industry_id,
                employmentType: w.job_type_id,
                startDate: toMonth(w.start_date),
                endDate: w.is_working_here ? "" : toMonth(w.end_date),
                current: w.is_working_here,
                responsibilities: w.responsibility,
                achievements: w.achievement,
            })) || []
        );

        setEducation(
            profile.educations?.map((e: any) => ({
                univTitle: e.school_name,
                univDegree: e.degree_id,
                univMajor: e.major,
                startDate: toMonth(e.start_date),
                endDate: e.is_currently_study ? "" : toMonth(e.end_date),
                current: e.is_currently_study,
            })) || []
        );

        setCertificate(
            profile.certifications?.map((c: any) => ({
                title: c.title,
                organization: c.issuer,
                issueDate: toMonth(c.issue_date),
                current: c.is_lifetime,
                expDate: c.is_lifetime ? "" : toMonth(c.expire_date),
            })) || []
        );

        setCountryId(String(profile.country_id || ""));
        setStateId(String(profile.state_id || ""));
        setCityId(String(profile.city_id || ""));
    }, [profile]);


    // ================== MODAL ==================
    const openModal = (type: string) => {
        setModalContent(type);
        setModalOpen(true);
    };

    const cleanWysiwygHTML = (html?: string) => {
        if (!html) return "";

        return html
            // hapus p kosong
            .replace(/<p><\/p>/g, "")
            .replace(/<p>\s*<\/p>/g, "")
            // hapus <p> di dalam <li>
            .replace(/<li><p>/g, "<li>")
            .replace(/<\/p><\/li>/g, "</li>");
        };

    // ================== EXPORT CV ==================
    const handleExportCV = () => {
        exportCV({
            ...basicInformationCv[0],

            workExperience: workExperience.map((exp) => ({
                ...exp,
                industryName: findLabel(industries, exp.industry),
                employmentTypeName: findLabel(jobTypes, exp.employmentType),
                responsibilities: cleanWysiwygHTML(exp.responsibilities),
                achievements: cleanWysiwygHTML(exp.achievements),
            })),


            education: education.map((edu) => ({
                ...edu,
                degreeName: findLabel(degrees, edu.univDegree),
            })),

            skill: skill.map((s) => ({
                skillName: findLabel(skillSet, s.skillName),
            })),

            language: language.map((s) => ({
            languageName: findLabel(languageSet, s.languageId, "name", "en"),
            proficiencyName: findLabel(proficiencies, s.profiencyId, "name", "en"),
            })),

            salary,
            project,
            certificate,
            resume,
        });
    };

    return (
        <MainLayoutUser>
            {loading && <PageLoader />}

            <section className="w-full pt-6 relative">
                <div className="container-section mx-auto px-6">
                    <SectionBox className="mt-24">
                        <div className="mx-auto space-y-6">



                            <div className="flex flex-col-reverse md:grid md:grid-cols-3 gap-6">

                                <div className="md:col-span-2 space-y-6">
                                    <UserBasicInformation
                                        onExport={handleExportCV}
                                        openModal={openModal}
                                        basicInformation={basicInformation ?? []}
                                        basicInformationCv={basicInformationCv ?? []}
                                        countries={countries}
                                        states={states}
                                        cities={cities}
                                        genders={genders}
                                        degrees={degrees}
                                        martialStatuses={martialStatus}
                                        experiences={experiences}
                                        experienceLevels={experienceLevels}
                                        lang={lang}
                                    />
                                    <UserWorkExperience
                                        openModal={openModal}
                                        experiences={workExperience ?? []}
                                        industries={industries}
                                        jobTypes={jobTypes}
                                        lang={lang}
                                    />
                                    <UserEducation
                                        openModal={openModal}
                                        educations={education ?? []}
                                        degrees={degrees}
                                        lang={lang}
                                    />
                                    <UserSalary
                                        openModal={openModal}
                                        salaries={salary ?? []}
                                        lang={lang}
                                    />
                                    <UserCertification
                                        openModal={openModal}
                                        certificates={certificate}
                                        lang={lang}
                                    />
                                    <UserLanguage
                                        openModal={openModal}
                                        languagesProf={language ?? []}
                                        languages={languageSet}
                                        proficiencies={proficiencies}
                                        lang={lang}
                                    />
                                    <UserSkill
                                        openModal={openModal}
                                        skills={skill ?? []}
                                        skillSet={skillSet}
                                        lang={lang}
                                    />
                                    {/* <UserProject
                                        openModal={openModal}
                                        projects={project ?? []}
                                        lang={lang}
                                    /> */}
                                    <UserResume
                                        openModal={openModal}
                                        resume={resume}
                                        lang={lang}
                                    />

                                </div>

                                <div className="space-y-6">
                                    <SidebarRight />
                                </div>
                            </div>

                            <EditProfileModal
                                isOpen={modalOpen}
                                onClose={() => setModalOpen(false)}
                                type={modalContent}
                                setWorkExperience={setWorkExperience}
                                workExperience={workExperience}
                                setEducation={setEducation}
                                education={education}
                                setBasicInformation={setBasicInformation}
                                basicInformation={basicInformation}
                                setSalary={setSalary}
                                salary={salary}
                                skill={skill}
                                setSkill={setSkill}
                                resume={resume}
                                setResume={setResume}
                                certificate={certificate}
                                setCertificate={setCertificate}
                                project={project}
                                setProject={setProject}
                                language={language}
                                setLanguage={setLanguage}
                                lang={lang}
                            />
                        </div>

                    </SectionBox>
                </div>
            </section>

        </MainLayoutUser>
    );
}
